<template>
  <GfrLayoutAside class="app-anchor-aside">
    <GfrScrollArea
      class="app-anchor-catalog-wrapper"
      :initial-y="scrollAreaY"
      :dir="$route.params.lang === 'ar' ? 'rtl' : 'ltr'"
    >
      <div class="relative">
        <div v-show="anchor" ref="activeBg" class="app-anchor-catalog-active" :style="activeBgStyle">
          <IconsNormalArrow class="app-anchor-catalog-arrow" />
        </div>
        <ul>
          <li ref="catalogItems" class="app-anchor-catalog-item" v-for="(item, index) in catalog" :key="index">
            <span
              class="app-anchor-catalog-link"
              :class="{ active: item.link === anchor, 'click-trigger': clickTriggerIdx === index }"
              @click="scrollToModule(item.link, index)"
            >
              <span>{{ item.title }}</span>
            </span>
          </li>
        </ul>
      </div>
    </GfrScrollArea>
  </GfrLayoutAside>
</template>
<script lang="ts" setup>
interface Anchor {
  title: string
  link: string
}
const props = defineProps({
  catalog: {
    type: Array as PropType<Anchor[]>,
    required: true
  }
})
const anchor = ref('')
const scrollAreaY = ref(0)
const catalogItems = ref<HTMLElement[]>([])
const clickTriggerIdx = ref(-1)
const currentIndex = ref(-1)
// const isScrolling = ref(false)
// const scrollTimer = ref<number | null>(null)
const activeBgStyle = computed(() => {
  const section = catalogItems.value[currentIndex.value]
  if (section) {
    return {
      height: `${section.offsetHeight}px`,
      transform: `translateY(${Math.max(0, section.offsetTop)}px)`
    }
  }
  return {}
})
const calculateElementPosition = (element: HTMLElement, scrollTop: number) => {
  const rect = element.getBoundingClientRect()
  const viewportHeight = window.innerHeight
  // 计算元素顶部和底部相对于视口的位置
  const elementTop = rect.top + scrollTop
  const elementBottom = elementTop + rect.height

  // 计算元素在视口中的可见部分
  const visibleTop = Math.max(elementTop, scrollTop)
  const visibleBottom = Math.min(elementBottom, scrollTop + viewportHeight)
  const visibleHeight = visibleBottom - visibleTop

  return {
    elementTop,
    elementBottom,
    visibleHeight,
    visibility: visibleHeight / rect.height
  }
}
// const handleScrollEnd = () => {
//   clickTriggerIdx.value = -1
//   isScrolling.value = false
// }
// const debouncedScrollEnd = () => {
//   if (scrollTimer.value) {
//     clearTimeout(scrollTimer.value)
//   }
//   scrollTimer.value = window.setTimeout(handleScrollEnd, 300)
// }
const handleScroll = () => {
  // if (clickTriggerIdx.value !== -1) return
  // isScrolling.value = true
  // debouncedScrollEnd()
  const sections = props.catalog.map((item) => {
    return document.querySelector(`#${item.link}`)
  })
  let maxVisibility = 0
  let mostVisibleSection = null
  const viewportHeight = window.innerHeight
  const scrollTop = document.documentElement.scrollTop || document.body.scrollTop
  const scrollHeight = document.documentElement.scrollHeight
  const atBottom = scrollHeight - scrollTop - viewportHeight <= 20
  sections.forEach((section, index) => {
    if (section) {
      const { visibility } = calculateElementPosition(section as HTMLElement, scrollTop)
      if (visibility > maxVisibility) {
        maxVisibility = visibility
        mostVisibleSection = section
        currentIndex.value = index
      }
    }
  })
  if (atBottom && scrollTop > 20) {
    mostVisibleSection = sections[sections.length - 1]
    currentIndex.value = sections.length - 1
    scrollToActiveLink()
  }
  if (scrollTop <= 20) {
    mostVisibleSection = sections[0]
    currentIndex.value = 0
    scrollToActiveLink()
  }
  if (mostVisibleSection) {
    const id = (mostVisibleSection as HTMLElement).id
    anchor.value = id
    scrollToActiveLink()
  }
}

const scrollToModule = (link: string, index: number) => {
  if (index === currentIndex.value || clickTriggerIdx.value > -1) return
  const scrollTop = document.documentElement.scrollTop || document.body.scrollTop
  const section = document.querySelector(`#${link}`) as HTMLElement
  if (section) {
    // anchor.value = section.id
    clickTriggerIdx.value = index
    // currentIndex.value = index
    // 计算目标滚动位置
    const { elementTop } = calculateElementPosition(section, scrollTop)
    const viewportHeight = window.innerHeight
    // 计算滚动位置，使元素在视口中居中
    const targetScrollTop = elementTop - (viewportHeight - section.offsetHeight) / 2 + 20
    window.scrollTo({
      top: Math.max(0, targetScrollTop),
      behavior: 'smooth'
    })
    const newScrollTop = document.documentElement.scrollTop || document.body.scrollTop
    // 如果滚动位置没有发生变化，也进入锚点切换逻辑
    setTimeout(() => {
      if (newScrollTop === scrollTop) {
        anchor.value = section.id
        currentIndex.value = index
      }
      clickTriggerIdx.value = -1
    }, 300)
    scrollToActiveLink()
  }
}
const scrollToActiveLink = () => {
  const activeLink = catalogItems.value[currentIndex.value]
  if (activeLink) {
    scrollAreaY.value = Math.max(0, activeLink.offsetTop)
  }
}
onMounted(() => {
  handleScroll() // Initial check
  window.addEventListener('scroll', handleScroll)
})
onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
  // if (scrollTimer.value) {
  //   clearTimeout(scrollTimer.value)
  // }
})
</script>

<style lang="scss" scoped>
.app-anchor-aside {
  @apply sticky top-[6.5rem] h-[calc(100vh-6.5rem)] w-[264px] flex-shrink-0 ltr:border-r rtl:border-l border-[#4D5158];
}
.app-anchor-catalog-wrapper {
  @apply relative w-full text-2xl h-[calc(100%-1.25rem)];
}
.app-anchor-catalog-item {
  @apply mb-2;
}
.app-anchor-catalog-link {
  @apply relative capitalize inline-flex min-h-15 py-2 w-full items-center px-[2.125rem] transition-colors duration-300;
  &.active,
  &.click-trigger {
    @apply text-primary;
  }
}
.app-anchor-catalog-active {
  @apply absolute left-0 top-0 flex w-full ltr:bg-gradient-to-l rtl:bg-gradient-to-r from-transparent to-[#363A41];
  transition:
    transform 0.3s ease,
    height 0.3s ease;
}
.app-anchor-catalog-arrow {
  @apply absolute ltr:-right-1 rtl:-left-1 top-1/2 h-4 w-4 -translate-y-1/2 ltr:rotate-90 rtl:-rotate-90 text-primary;
}
</style>
